import { coreTypes } from "@core/core-types.di";
import { HttpFailedRequestError } from "@core/data/infrastructures/http/errors/http-failed-request.error";
import { type Http } from "@core/data/infrastructures/http/http";
import { HttpErrorCodeEnum } from "@core/data/infrastructures/http/http-error-response";
import { FallbackError } from "@core/domain/errors/fallback.error";
import { ValidationError } from "@core/domain/errors/validation.error";
import { Pagination } from "@core/domain/models/pagination";
import { Either } from "@core/domain/types/either";
import { plainToClass } from "class-transformer";
import { inject, injectable } from "inversify";

import { CreateEntityApiKeyBody } from "@entity-api-keys/data/dto/create-entity-api-key.body";
import {
    EntityApiKeyCreatedDto,
    EntityApiKeyDto,
} from "@entity-api-keys/data/dto/entity-api-key.dto";
import {
    EntityApiKeysDto,
    EntityApiKeysQuery,
} from "@entity-api-keys/data/dto/entity-api-keys.dto";
import { UpdateEntityApiKeyBody } from "@entity-api-keys/data/dto/update-entity-api-key.body";
import { EntityApiKeyMapper } from "@entity-api-keys/data/mappers/entity-api-key.mapper";
import { EntityApiKeysMapper } from "@entity-api-keys/data/mappers/entity-api-keys.mapper";
import { CreateEntityApiKey } from "@entity-api-keys/domain/models/create-entity-api-key.model";
import {
    CreatedEntityApiKey,
    EntityApiKey,
    EntityApiKeys,
} from "@entity-api-keys/domain/models/entity-api-key.model";
import { PublicApiUsageStats } from "@entity-api-keys/domain/models/public-api-usage-stats.model";
import { UpdateEntityApiKey } from "@entity-api-keys/domain/models/update-entity-api-key.model";

@injectable()
export class EntityApiKeysDatasource {
    constructor(
        @inject(coreTypes.infrastructure.Http) private readonly http: Http,
        @inject(EntityApiKeyMapper)
        private readonly entityApiKeyMapper: EntityApiKeyMapper,
        @inject(EntityApiKeysMapper)
        private readonly entityApiKeysMapper: EntityApiKeysMapper,
    ) {}

    async fetchById(id: number): Promise<Either<FallbackError, EntityApiKey>> {
        const result = await this.http.get<EntityApiKeyDto>(
            `/entity_api_keys/${id}/`,
        );

        return result
            .mapLeft(() => new FallbackError())
            .flatMap((response) => {
                const dto = plainToClass(EntityApiKeyDto, response.data);
                const apiKey = this.entityApiKeyMapper.map(dto);
                if (!apiKey) return Either.Left(new FallbackError());
                return Either.Right(apiKey);
            });
    }

    async fetchAllBy(
        pagination: Pagination,
        search?: string,
        entityId?: number,
        createdAfter?: string,
        createdBefore?: string,
        isActive?: boolean,
        scopes?: string[],
    ): Promise<Either<FallbackError, EntityApiKeys>> {
        const query: EntityApiKeysQuery = {
            limit: pagination.pageSize,
            offset: pagination.offset,
        };

        if (search) query.search = search;
        if (entityId !== undefined) query.entity = entityId;
        if (createdAfter) query.created_after = createdAfter;
        if (createdBefore) query.created_before = createdBefore;
        if (isActive !== undefined) query.is_active = isActive;
        if (scopes && scopes.length > 0) query.scopes = scopes.join(",");

        const result = await this.http.get<EntityApiKeysDto>(
            "/entity_api_keys/",
            { query },
        );

        return result
            .mapLeft(() => new FallbackError())
            .map((response) =>
                this.entityApiKeysMapper.map(
                    plainToClass(EntityApiKeysDto, response.data),
                ),
            );
    }

    async create(
        model: CreateEntityApiKey,
    ): Promise<Either<ValidationError | FallbackError, CreatedEntityApiKey>> {
        const body: CreateEntityApiKeyBody = {
            entity: model.entityId,
            name: model.name,
            scopes: model.scopes,
            is_active: model.isActive,
        };

        const result = await this.http.post<
            EntityApiKeyCreatedDto,
            CreateEntityApiKeyBody
        >("/entity_api_keys/", body);

        return result
            .mapLeft((error) => {
                if (
                    error instanceof HttpFailedRequestError &&
                    error.errorCode === HttpErrorCodeEnum.GenericError
                ) {
                    return new ValidationError(error.data);
                }

                return new FallbackError();
            })
            .flatMap((response) => {
                const dto = plainToClass(EntityApiKeyCreatedDto, response.data);
                const created = this.entityApiKeyMapper.mapCreated(dto);
                if (!created) return Either.Left(new FallbackError());
                return Either.Right(created);
            });
    }

    async update(
        model: UpdateEntityApiKey,
    ): Promise<Either<ValidationError | FallbackError, EntityApiKey>> {
        const body: UpdateEntityApiKeyBody = {
            name: model.name,
            scopes: model.scopes,
        };

        if (model.entityId !== undefined) body.entity = model.entityId;
        if (model.isActive !== undefined) body.is_active = model.isActive;

        const result = await this.http.patch<
            EntityApiKeyDto,
            UpdateEntityApiKeyBody
        >(`/entity_api_keys/${model.id}/`, body);

        return result
            .mapLeft((error) => {
                if (
                    error instanceof HttpFailedRequestError &&
                    error.errorCode === HttpErrorCodeEnum.GenericError
                ) {
                    return new ValidationError(error.data);
                }

                return new FallbackError();
            })
            .flatMap((response) => {
                const dto = plainToClass(EntityApiKeyDto, response.data);
                const apiKey = this.entityApiKeyMapper.map(dto);
                if (!apiKey) return Either.Left(new FallbackError());
                return Either.Right(apiKey);
            });
    }

    async delete(id: number): Promise<Either<FallbackError, true>> {
        const result = await this.http.delete(`/entity_api_keys/${id}/`);
        return result.mapLeft(() => new FallbackError()).map(() => true);
    }

    async fetchUsageStats(
        period: string,
    ): Promise<Either<FallbackError, PublicApiUsageStats>> {
        const query = { period };

        const result = await this.http.get<PublicApiUsageStats>(
            "/public_api_usage_stats/",
            { query },
        );

        return result
            .mapLeft(() => new FallbackError())
            .map((response) => {
                const data = response.data;

                const mapped: PublicApiUsageStats = {
                    period: data.period,
                    from: data.from,
                    to: data.to,
                    totals: data.totals,
                    by_api_key: data.by_api_key,
                };

                if (data.top_scopes !== undefined) {
                    mapped.top_scopes = data.top_scopes;
                }

                return mapped;
            });
    }

    async exportUsageStatsXlsx(
        period: string,
    ): Promise<Either<FallbackError, Blob>> {
        const query = { period };

        const result = await this.http.get<Blob>(
            "/public_api_usage_stats/export_xlsx/",
            {
                responseType: "blob",
                query,
            },
        );

        return result
            .mapLeft(() => new FallbackError())
            .map((response) => response.data as Blob);
    }
}
